/**
 * Generated Marketing Asset
 *
 * Shared renderer for selected generated marketing visuals.
 *
 * Primary responsibilities:
 * - Render selected generated assets with consistent framing
 * - Apply light or dark presentation tones per asset
 *
 * Dependencies:
 * - next/image
 * - selected generated asset map
 * - lib/utils
 */

'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { useTheme } from 'next-themes'

import {
  selectedGeneratedAssets,
  type SelectedGeneratedAssetKey
} from '@/lib/marketing/selected-generated-assets'
import { cn } from '@/lib/utils'

type GeneratedMarketingAssetPresentation = 'auto' | 'open' | 'panel' | 'soft-stage'

interface GeneratedMarketingAssetProps {
  assetKey: SelectedGeneratedAssetKey
  className?: string
  contentClassName?: string
  frame?: boolean
  imageClassName?: string
  presentation?: GeneratedMarketingAssetPresentation
  priority?: boolean
  sizes?: string
}

export function GeneratedMarketingAsset({
  assetKey,
  className,
  contentClassName,
  frame = true,
  imageClassName,
  presentation = 'auto',
  priority = false,
  sizes = '100vw'
}: GeneratedMarketingAssetProps) {
  const [isMounted, setIsMounted] = useState(false)
  const asset = selectedGeneratedAssets[assetKey]
  const { resolvedTheme } = useTheme()
  const isDarkTheme = isMounted && resolvedTheme === 'dark'
  const resolvedSrc = !isMounted
    ? asset.src
    : isDarkTheme
      ? asset.darkSrc || asset.src
      : asset.lightSrc || asset.src
  const resolvedTone = !isMounted
    ? asset.tone
    : isDarkTheme
      ? asset.darkTone || asset.tone
      : asset.lightTone || asset.tone
  const resolvedPresentation: Exclude<GeneratedMarketingAssetPresentation, 'auto'> = !frame
    ? 'open'
    : presentation === 'auto'
      ? asset.opaque
        ? 'panel'
        : 'soft-stage'
      : presentation
  const imageFitClassName =
    (asset.fit || (asset.opaque ? 'cover' : 'contain')) === 'cover'
      ? 'object-cover'
      : 'object-contain'
  const stageShadowClassName = asset.opaque
    ? undefined
    : resolvedTone === 'dark'
      ? 'drop-shadow-[0_20px_42px_rgba(5,10,18,0.24)]'
      : 'drop-shadow-[0_28px_50px_rgba(148,163,184,0.34)]'
  const stageInsetClassName =
    imageFitClassName === 'object-cover' ? 'inset-0' : 'inset-[2%] sm:inset-[3%]'
  const panelInsetClassName =
    imageFitClassName === 'object-cover'
      ? 'inset-0'
      : asset.opaque
        ? 'inset-0'
        : 'inset-5 sm:inset-7'

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (resolvedPresentation === 'open') {
    return (
      <div className={cn('relative overflow-hidden rounded-[1.75rem]', className)}>
        <Image
          src={resolvedSrc}
          alt={asset.alt}
          fill
          priority={priority}
          sizes={sizes}
          className={cn(imageFitClassName, asset.mediaClassName, imageClassName)}
        />
      </div>
    )
  }

  if (resolvedPresentation === 'soft-stage') {
    return (
      <div className={cn('group relative isolate overflow-visible', className)}>
        <div
          className={cn(
            'pointer-events-none absolute inset-x-[6%] inset-y-[8%] rounded-[2.5rem] blur-2xl',
            resolvedTone === 'dark'
              ? 'bg-[radial-gradient(circle_at_top,rgba(52,74,106,0.18),rgba(7,12,20,0.08)_54%,rgba(7,12,20,0)_100%)]'
              : 'bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.96),rgba(236,240,247,0.76)_56%,rgba(236,240,247,0)_100%)]'
          )}
        />
        <div
          className={cn(
            'pointer-events-none absolute inset-x-[14%] bottom-[9%] h-[18%] rounded-full blur-3xl',
            resolvedTone === 'dark'
              ? 'bg-[radial-gradient(circle,rgba(84,109,143,0.1),rgba(8,15,28,0)_72%)]'
              : 'bg-[radial-gradient(circle,rgba(15,23,42,0.12),rgba(15,23,42,0)_72%)]'
          )}
        />
        {resolvedTone === 'light' ? (
          <div
            className="pointer-events-none absolute inset-0 opacity-20 mix-blend-multiply"
            style={{ backgroundImage: `url(${selectedGeneratedAssets.grainOverlay.src})` }}
          />
        ) : null}
        <div
          className={cn(
            'absolute transition-transform duration-700 ease-out group-hover:scale-[1.015]',
            stageInsetClassName,
            contentClassName
          )}
        >
          <Image
            src={resolvedSrc}
            alt={asset.alt}
            fill
            priority={priority}
            sizes={sizes}
            className={cn(
              imageFitClassName,
              stageShadowClassName,
              asset.mediaClassName,
              imageClassName
            )}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-[1.9rem] border shadow-[0_24px_70px_rgba(15,23,42,0.08)]',
        resolvedTone === 'dark'
          ? 'border-slate-800/70 bg-[radial-gradient(circle_at_top,rgba(64,91,126,0.1),transparent_42%),linear-gradient(180deg,rgba(10,15,22,0.98),rgba(6,10,16,0.98))]'
          : 'border-slate-200/80 bg-[radial-gradient(circle_at_top,rgba(190,202,220,0.28),transparent_42%),linear-gradient(180deg,rgba(255,255,255,0.96),rgba(239,241,245,0.92))]',
        className
      )}
    >
      {resolvedTone === 'light' ? (
        <div
          className="pointer-events-none absolute inset-0 opacity-30 mix-blend-multiply"
          style={{ backgroundImage: `url(${selectedGeneratedAssets.grainOverlay.src})` }}
        />
      ) : null}
      <div
        className={cn(
          'absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.015]',
          panelInsetClassName,
          contentClassName
        )}
      >
        <Image
          src={resolvedSrc}
          alt={asset.alt}
          fill
          priority={priority}
          sizes={sizes}
          className={cn(imageFitClassName, asset.mediaClassName, imageClassName)}
        />
      </div>
    </div>
  )
}
